import type { BaselineStatus, Condition, DeviceId, RecordingStatus } from './api'
import { readDailyStatus, type DailyStatusResult } from './dailyStatus'
import { isNotFound } from './problem'

/**
 * Reading `GET /users/{user_id}/home`, in exactly one place (Sprint 1.9.9 §1.4).
 *
 * ── WHY A SECOND READER ─────────────────────────────────────────────────────
 *
 * `/home` is the mirror's whole top section in one answer, and most of it is
 * selection the backend already made: which attempt is the latest, which
 * reading is the newest, which days the trail holds. Its one embedded Daily
 * Status (`reading.payload`) goes through `readDailyStatus` like every other
 * payload, so there is still one reader of THAT shape. What this file adds is
 * the envelope around it, narrowed structurally, and the handful of lookups the
 * map needs.
 *
 * ── WHAT IT NEVER DOES ──────────────────────────────────────────────────────
 *
 * It compares no number. The dot is `state_map.activation.value` and
 * `.balance.value` as sent; the corners, the ring's radius and the axis bounds
 * are looked up (the reading's own `state_map` first, `/home.display` second);
 * the reading's word and line are `state_map.display.word` and `.line`, and
 * `quadrant_reason` chooses a layout, never a word (1.9.9 D20). Which quadrant a
 * reading is in is the payload's `quadrant` token, tested for membership with
 * `Object.hasOwn` because it is external input and `'constructor' in {}` is
 * true (critique #25). This file is in `clientScience`'s SCORE_READING, so a
 * comparison written here needs a reason written down — and there are none.
 *
 * **Absent and null are the same** for every optional field (1.9.9 D3, D19).
 */

/* ── the quadrant tokens ─────────────────────────────────────────────────── */

/**
 * The four corners of the state map, as the package names them.
 *
 * Wire tokens, not words: the words for each are `state_map.display.quadrants`
 * or `/home.display.quadrants`. The ORDER is the design system's corner order
 * (`QuadrantMap.jsx`: top right, top left, bottom left, bottom right).
 */
export const QUADRANT_TOKENS = [
  'act_pos_bal_pos',
  'act_pos_bal_neg',
  'act_neg_bal_neg',
  'act_neg_bal_pos',
] as const

export type QuadrantToken = (typeof QUADRANT_TOKENS)[number]

const TOKEN_INDEX: Readonly<Record<QuadrantToken, true>> = {
  act_pos_bal_pos: true,
  act_pos_bal_neg: true,
  act_neg_bal_neg: true,
  act_neg_bal_pos: true,
}

/** The token, if it is one of the four; `null` for anything else, prototype keys included. */
export function quadrantToken(value: unknown): QuadrantToken | null {
  return typeof value === 'string' && Object.hasOwn(TOKEN_INDEX, value) ? (value as QuadrantToken) : null
}

/* ── the read model ──────────────────────────────────────────────────────── */

export interface HomeLatestView {
  recordingId: string
  status: RecordingStatus
  sessionDate: string
  /** A Problem code (`failed`, `empty`) or the package's QC token (`rejected`); null when scored. */
  reasonCode: string | null
}

export interface HomeReadingView {
  recordingId: string
  sessionDate: string
  /** `reading.payload` through `readDailyStatus`, so an unreadable payload is a state, not a crash. */
  result: DailyStatusResult
  /**
   * The payload's top-level `direction_labels` (schema 1.5): direction code →
   * the package's words. `readDailyStatus` has no use for it, so it is carried
   * here; null on a stored 1.3/1.4 day, which has none.
   */
  directionLabels: Readonly<Record<string, string>> | null
}

/** One scored day before the reading. Flattened on the wire: a number or null, never an object. */
export interface HomeTrailPoint {
  sessionDate: string
  activation: number | null
  balance: number | null
  quadrant: QuadrantToken | null
}

export interface HomeDayView {
  date: string
  recorded: boolean
  quadrant: QuadrantToken | null
}

export interface HomeHintView {
  code: string
  /** Backend-authored, rendered verbatim (1.9.9 D10). */
  text: string
}

export interface HomeBaselinesView {
  /** The ONE headband both baselines were read for (1.9.9 D18). */
  deviceId: DeviceId
  EC: BaselineStatus
  EO: BaselineStatus
}

/**
 * `/home.note`: the reading's stored daily note (Sprint 1.9.10 B12's reviewed
 * contract). The headline and body are the backend's stored words, verbatim.
 */
export interface HomeNoteView {
  recordingId: string
  headline: string
  body: string
  /**
   * Who wrote it, `model` or `template`. It picks the words of the note's "How
   * notes are written" disclosure (R65, superseding U5's "carried, never
   * rendered"); it is never shown as a badge. Null when absent or unknown,
   * because the web deploys before the backend that sends it, and a null
   * disclosure claims nothing about how the note was written.
   */
  source: NoteSource | null
}

export type NoteSource = 'model' | 'template'

const NOTE_SOURCES: Readonly<Record<NoteSource, true>> = { model: true, template: true }

/** `/home.display`: the installed release's static map words, null before eeg 0.11.0 (1.9.9 D1). */
export interface HomeDisplayView {
  quadrants: Readonly<Record<string, string>>
  usual: string
  nearUsual: string
  deadzoneZ: number
  axisClip: readonly [number, number]
  directionLabels: Readonly<Record<string, string>>
  /** Carried for `/state`'s axis names and reason tags; not read by Home. */
  axes: Readonly<Record<string, unknown>>
  reasons: Readonly<Record<string, unknown>>
}

export interface HomeSummaryView {
  userId: string
  condition: Condition
  /** `YYYY-MM-DD`, the server's today in the zone it resolved. */
  today: string
  timezone: string
  latest: HomeLatestView | null
  reading: HomeReadingView | null
  trail: readonly HomeTrailPoint[]
  baseline: HomeBaselinesView
  hint: HomeHintView | null
  secondaryHint: HomeHintView | null
  week: readonly HomeDayView[]
  processing: { jobId: string } | null
  note: HomeNoteView | null
  display: HomeDisplayView | null
}

export type HomeRead =
  | { readable: true; summary: HomeSummaryView }
  | { readable: false; detail: string }

/* ── narrowing helpers. Structural only; nothing here judges a value. ────── */

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const numberOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const stringOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null)

/** A map whose every value is a string, or null. Never partially kept: a half map is not the map. */
function stringMap(v: unknown): Readonly<Record<string, string>> | null {
  if (!isObject(v)) return null
  const entries = Object.entries(v)
  return entries.every(([, value]) => typeof value === 'string') ? (v as Record<string, string>) : null
}

/** `[lo, hi]` — two finite numbers, as `axis_clip` is sent. */
function pairOrNull(v: unknown): readonly [number, number] | null {
  if (!Array.isArray(v) || v.length !== 2) return null
  const lo = numberOrNull(v[0])
  const hi = numberOrNull(v[1])
  return lo === null || hi === null ? null : [lo, hi]
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/
const isDate = (v: unknown): v is string => typeof v === 'string' && CALENDAR_DATE.test(v)

const DEVICES: Readonly<Record<DeviceId, true>> = { brainbit: true, brainbit_pro: true }

/** Thrown inside the reader and caught at its edge, so every field fails the same way. */
class Unreadable extends Error {}

function fail(field: string): never {
  throw new Unreadable(field)
}

/** Absent or null: nothing. Anything else must be what `read` says, or the summary is unreadable. */
function optional<T>(raw: Record<string, unknown>, key: string, read: (v: unknown) => T | null): T | null {
  const value = raw[key]
  if (value === undefined || value === null) return null
  return read(value) ?? fail(key)
}

function baselineOf(v: unknown): BaselineStatus | null {
  if (!isObject(v)) return null
  const counts = [v.n_sessions, v.n_distinct_days, v.days_confirmed_remaining]
  if (!counts.every((n) => numberOrNull(n) !== null)) return null
  if (typeof v.low_confidence_flag !== 'boolean') return null
  return v as unknown as BaselineStatus
}

function hintOf(v: unknown): HomeHintView | null {
  if (!isObject(v)) return null
  const code = stringOrNull(v.code)
  const text = stringOrNull(v.text)
  return code === null || text === null ? null : { code, text }
}

/**
 * One trail point. The axes must be a number or null — the flattened form the
 * contract pins (critique #11) — because an axis object would reach the CSS as
 * `--act: [object Object]` and fail nowhere. So an object is unreadable, loudly.
 */
function trailPointOf(v: unknown): HomeTrailPoint | null {
  if (!isObject(v) || !isDate(v.session_date)) return null
  const axis = (a: unknown): number | null | undefined =>
    a === undefined || a === null ? null : numberOrNull(a) ?? undefined
  const activation = axis(v.activation)
  const balance = axis(v.balance)
  if (activation === undefined || balance === undefined) return null
  return { sessionDate: v.session_date, activation, balance, quadrant: quadrantToken(v.quadrant) }
}

/**
 * One note. `source` is optional here although the contract requires it: a
 * backend from before B12 sends none, and a value this build does not know is
 * not a reason to hide the words, so both read as null. The words themselves
 * are required, as they always were: a malformed note is still unreadable.
 */
function noteOf(v: unknown): HomeNoteView | null {
  if (!isObject(v)) return null
  const recordingId = stringOrNull(v.recording_id)
  const headline = stringOrNull(v.headline)
  const body = stringOrNull(v.body)
  if (recordingId === null || headline === null || body === null) return null
  const source = stringOrNull(v.source)
  return { recordingId, headline, body, source: source !== null && Object.hasOwn(NOTE_SOURCES, source) ? (source as NoteSource) : null }
}

function dayOf(v: unknown): HomeDayView | null {
  if (!isObject(v) || !isDate(v.date) || typeof v.recorded !== 'boolean') return null
  return { date: v.date, recorded: v.recorded, quadrant: quadrantToken(v.quadrant) }
}

function displayOf(v: unknown): HomeDisplayView | null {
  if (!isObject(v)) return null
  const quadrants = stringMap(v.quadrants)
  const directionLabels = stringMap(v.direction_labels)
  const usual = stringOrNull(v.usual)
  const nearUsual = stringOrNull(v.near_usual)
  const deadzoneZ = numberOrNull(v.deadzone_z)
  const axisClip = pairOrNull(v.axis_clip)
  if (quadrants === null || directionLabels === null || usual === null || nearUsual === null) return null
  if (deadzoneZ === null || axisClip === null) return null
  return {
    quadrants,
    usual,
    nearUsual,
    deadzoneZ,
    axisClip,
    directionLabels,
    axes: isObject(v.axes) ? v.axes : {},
    reasons: isObject(v.reasons) ? v.reasons : {},
  }
}

/**
 * Read a `/home` answer.
 *
 * Returns the view, or an explicit unreadable state naming the first field that
 * was not what this build expects. Never a partial view: a summary with its
 * baselines missing would render "None yet" about a history that exists.
 */
export function readHomeSummary(raw: unknown): HomeRead {
  try {
    if (!isObject(raw)) fail('the summary')
    const userId = stringOrNull(raw.user_id) ?? fail('user_id')
    const condition = raw.condition === 'EC' || raw.condition === 'EO' ? raw.condition : fail('condition')
    const today = isDate(raw.today) ? raw.today : fail('today')
    const timezone = stringOrNull(raw.timezone) ?? fail('timezone')

    const trailRaw = raw.trail ?? []
    if (!Array.isArray(trailRaw)) fail('trail')
    const trail = trailRaw.map((p) => trailPointOf(p) ?? fail('trail'))

    const weekRaw = raw.week
    if (!Array.isArray(weekRaw) || weekRaw.length !== 7) fail('week')
    const week = weekRaw.map((d) => dayOf(d) ?? fail('week'))

    const baselineRaw = raw.baseline
    if (!isObject(baselineRaw)) fail('baseline')
    const deviceId = stringOrNull(baselineRaw.device_id)
    const baseline: HomeBaselinesView = {
      deviceId: deviceId !== null && Object.hasOwn(DEVICES, deviceId) ? (deviceId as DeviceId) : fail('baseline'),
      EC: baselineOf(baselineRaw.EC) ?? fail('baseline'),
      EO: baselineOf(baselineRaw.EO) ?? fail('baseline'),
    }

    const summary: HomeSummaryView = {
      userId,
      condition,
      today,
      timezone,
      latest: optional(raw, 'latest', (v) => {
        if (!isObject(v) || !isDate(v.session_date)) return null
        const recordingId = stringOrNull(v.recording_id)
        const status = stringOrNull(v.status)
        if (recordingId === null || status === null) return null
        const reason = v.reason_code ?? null
        if (reason !== null && typeof reason !== 'string') return null
        return { recordingId, status: status as RecordingStatus, sessionDate: v.session_date, reasonCode: reason }
      }),
      reading: optional(raw, 'reading', (v) => {
        if (!isObject(v) || !isDate(v.session_date) || !isObject(v.payload)) return null
        const recordingId = stringOrNull(v.recording_id)
        if (recordingId === null) return null
        return {
          recordingId,
          sessionDate: v.session_date,
          result: readDailyStatus(v.payload),
          directionLabels: stringMap(v.payload.direction_labels),
        }
      }),
      trail,
      baseline,
      hint: optional(raw, 'hint', hintOf),
      secondaryHint: optional(raw, 'secondary_hint', hintOf),
      week,
      processing: optional(raw, 'processing', (v) => {
        const jobId = isObject(v) ? stringOrNull(v.job_id) : null
        return jobId === null ? null : { jobId }
      }),
      note: optional(raw, 'note', noteOf),
      display: optional(raw, 'display', displayOf),
    }
    // The note is the READING's or it is not shown (UI-GAP-01 U5). The backend
    // drops a note whose recording is not the reading's; one that still arrives
    // is refused here, so no consumer can date one recording's words to another's
    // day. It is dropped, not failed: the reading's own summary still shows, as
    // Home's fallback for an unrelated note has always done.
    if (summary.note !== null && summary.note.recordingId !== summary.reading?.recordingId) return { readable: true, summary: { ...summary, note: null } }
    return { readable: true, summary }
  } catch (error) {
    if (!(error instanceof Unreadable)) throw error
    return {
      readable: false,
      detail: `Your summary could not be read: its ${error.message} is not in a shape this version of the app knows.`,
    }
  }
}

/* ── the provider's state, shared with the nav and /state ────────────────── */

/**
 * What the shell's one `/home` read has become (1.9.9 P6, D33).
 *
 *  - `ready` — a readable summary. `reloadError` is a later read that failed:
 *    the summary stays on screen and the error is shown beside it, so a blip
 *    never swaps the mirror for the old Home.
 *  - `absent` — a 404 whose code is `not_found`: an API from before `/home`
 *    existed. The 1.9.6 Home is fully usable in this mode. Retired after S9.
 *  - `failed` — anything else, including an unreadable 200 (`detail`).
 */
export type HomeSummaryState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: HomeSummaryView; reloadError: unknown }
  | { kind: 'absent' }
  | { kind: 'failed'; error: unknown; detail: string | null }

/**
 * The old API, and nothing else (1.9.9 D33): a 404 whose Problem code is the
 * backend's unknown-route `not_found`. `/home` itself never answers 404 for a
 * known user (D26), so this is a server from before the route existed. A 404
 * with any other code, or with no Problem at all (a proxy's), is a failure.
 */
export function isOldHomeApi(error: unknown): boolean {
  if (!isNotFound(error)) return false
  return (error as { problem?: { code?: unknown } }).problem?.code === 'not_found'
}

/**
 * Whether the reading on `/home` carries a state map at all — the one test the
 * nav's State entry and `/state` share (1.9.9 P6). It reads the RESULT of
 * `readDailyStatus`, so an unreadable payload has no map.
 */
export function hasStateMap(state: HomeSummaryState): boolean {
  if (state.kind !== 'ready') return false
  const result = state.summary.reading?.result
  return result !== undefined && result.readable && result.research.stateMap !== null
}

/**
 * The browser's IANA zone, for `/home`'s `timezone` query — or undefined, which
 * the server reads as UTC.
 *
 * Checked against the backend's own pattern (`contracts/home.py`), because a
 * malformed key is a 400 and would turn the whole mirror into a failure notice
 * over a string nobody on the page chose.
 */
const TIMEZONE_KEY = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/

export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' && TIMEZONE_KEY.test(zone) ? zone : undefined
  } catch {
    return undefined
  }
}

/* ── the map, looked up ──────────────────────────────────────────────────── */

/**
 * Everything the map card draws from one reading, looked up and never computed.
 *
 * `null` when the reading has no `state_map` (flags off) or is not readable.
 * Stored 1.3/1.4 readings carry no `display` block, so `word`, `line` and the
 * reading's own corners are null for them; the corners, the ring's radius and
 * the axis bounds then come from `/home.display`, which supplies geometry and
 * corner names only, never a reading's word (1.9.9 D1, C22).
 */
export interface MirrorStateMap {
  /** `state_map.activation.value`, verbatim. */
  act: number | null
  /** `state_map.balance.value`, verbatim. */
  bal: number | null
  quadrant: QuadrantToken | null
  /** `quadrant_reason`, for layout only (1.9.9 D20). */
  reason: string | null
  nearUsual: boolean | null
  word: string | null
  line: string | null
  corners: Readonly<Record<string, string>> | null
  /** The ring's label, `display.usual`. */
  usual: string | null
  dz: number | null
  clip: readonly [number, number] | null
  validated: boolean | null
  badge: string | null
}

const axisValue = (axis: unknown): number | null => (isObject(axis) ? numberOrNull(axis.value) : null)

export function mirrorStateMap(
  result: DailyStatusResult | null | undefined,
  display: HomeDisplayView | null,
): MirrorStateMap | null {
  if (result == null || !result.readable) return null
  const sm: unknown = result.research.stateMap
  if (!isObject(sm)) return null
  const own = isObject(sm.display) ? sm.display : null
  const act = axisValue(sm.activation)
  const bal = axisValue(sm.balance)
  const dz = numberOrNull(sm.deadzone_z) ?? display?.deadzoneZ ?? null
  return {
    act,
    bal,
    quadrant: quadrantToken(sm.quadrant),
    reason: stringOrNull(sm.quadrant_reason),
    nearUsual: typeof sm.near_usual === 'boolean' ? sm.near_usual : null,
    word: stringOrNull(own?.word),
    line: stringOrNull(own?.line),
    corners: stringMap(own?.quadrants) ?? display?.quadrants ?? null,
    usual: stringOrNull(own?.usual) ?? display?.usual ?? null,
    dz,
    clip: pairOrNull(sm.axis_clip) ?? display?.axisClip ?? null,
    validated: typeof sm.validated === 'boolean' ? sm.validated : null,
    badge: stringOrNull(sm.badge),
  }
}

/**
 * The corners' words by position — activation up, balance right — when the
 * dictionary has all four, and none otherwise: three corners named and one
 * blank would read as a corner that does not exist. One helper for Home and
 * `/state`, so the two maps cannot name their corners differently.
 */
export function cornerWords(words: Readonly<Record<string, string>> | null): { tl: string; tr: string; bl: string; br: string } | null {
  if (words === null) return null
  const tokens = ['act_pos_bal_neg', 'act_pos_bal_pos', 'act_neg_bal_neg', 'act_neg_bal_pos'] as const
  if (!tokens.every((t) => Object.hasOwn(words, t))) return null
  return { tl: words[tokens[0]], tr: words[tokens[1]], bl: words[tokens[2]], br: words[tokens[3]] }
}

/* ── what moved ──────────────────────────────────────────────────────────── */

/**
 * One card of `ui_scores`, as a chip: its title, and — only where the payload
 * gives one — its direction and the words for it.
 */
export interface MirrorChip {
  key: string
  title: string
  /** `ui_scores.label_directions[key]`: a direction code, or null for none (1.9.9 P2). */
  code: string | null
  /** `direction_labels[code]`, the package's words for it; null with no code or no words. */
  words: string | null
}

/**
 * "What moved": every card `ui_scores.display` names, in its order, except the
 * ones `ui_scores.validity` says were suppressed — a suppressed card has no
 * reading to describe. A card with no direction (Calm and Focus stability, by
 * P2) is its title alone. Lookups only: which way a card moved is the
 * package's `label_directions`, and how to say it is its `direction_labels`.
 */
export function mirrorChips(reading: HomeReadingView | null): MirrorChip[] {
  const result = reading?.result
  if (result == null || !result.readable) return []
  const ui: unknown = result.research.uiScores
  if (!isObject(ui) || !isObject(ui.display)) return []
  const validity = isObject(ui.validity) ? ui.validity : {}
  const directions = stringMap(ui.label_directions) ?? {}
  const words = reading?.directionLabels ?? {}
  return Object.entries(ui.display).flatMap(([key, entry]): MirrorChip[] => {
    const valid = validity[key]
    if (isObject(valid) && valid.suppressed === true) return []
    const title = isObject(entry) ? stringOrNull(entry.title) : null
    if (title === null) return []
    const code = Object.hasOwn(directions, key) ? directions[key] : null
    return [{ key, title, code, words: code !== null && Object.hasOwn(words, code) ? words[code] : null }]
  })
}

/* ── /state, looked up ───────────────────────────────────────────────────── */

/** The two axes, in the order `/state` lists them. Wire keys, not words. */
const STATE_AXES = ['activation', 'balance'] as const

/** One axis of the state map, as `/state` lists it (Sprint 1.9.9 S6). */
export interface StateAxisView {
  /** The payload key: the row's name, in mono, when the display block gives none. */
  key: (typeof STATE_AXES)[number]
  /** `display.axes[key].name` — "the axis's name", the package's word (D22). */
  name: string | null
  /** `display.axes[key].pos` / `.neg`, for the map's legend; null unless both are strings. */
  ends: { pos: string; neg: string } | null
  value: number | null
  confidence: number | null
  /** `evidence_tier`, only where the payload carries one. */
  tier: string | null
  /** The axis's own `reason`, when its value is withheld. */
  reason: string | null
  /** `display.reasons[reason].line`, the package's sentence for that token. */
  reasonLine: string | null
}

/** `balance_score`, as the state view reads it: the value and its window, or the reason there is none (D29). */
export interface StateTrendView {
  /** The 0–100 score, verbatim. Null means no number, and `reason` says why. */
  value: number | null
  nDays: number | null
  windowDays: number | null
  reason: string | null
  reasonWords: { word: string | null; line: string | null } | null
  badge: string | null
  validated: boolean | null
}

export interface StateView {
  map: MirrorStateMap
  axes: readonly StateAxisView[]
  /** `state_map.warnings`, verbatim tokens. */
  warnings: readonly string[]
  trend: StateTrendView | null
}

/** A dictionary entry by token, own keys only (critique #25). */
function entryOf(dictionary: unknown, token: string | null): Record<string, unknown> | null {
  if (token === null || !isObject(dictionary) || !Object.hasOwn(dictionary, token)) return null
  const entry = dictionary[token]
  return isObject(entry) ? entry : null
}

/**
 * Everything `/state` draws from one reading, looked up and never computed.
 *
 * The map is `mirrorStateMap`'s, so Home and `/state` place the same reading
 * the same way. The axis names, their ends and the reason words come from the
 * reading's own `state_map.display` first and `/home.display` second — the
 * same order as the corners (critique #24) — and a reason's words are only
 * ever the words FOR THAT TOKEN: `reasons[...]` names the axis and balance
 * tags, never the reading (D20). Null when the reading carries no state map.
 */
export function stateViewOf(
  result: DailyStatusResult | null | undefined,
  display: HomeDisplayView | null,
): StateView | null {
  const map = mirrorStateMap(result, display)
  if (map === null || result == null || !result.readable) return null
  const sm = result.research.stateMap as unknown as Record<string, unknown>
  const own = isObject(sm.display) ? sm.display : null
  const axesWords: unknown = isObject(own?.axes) ? own.axes : display?.axes ?? null
  const reasons: unknown = isObject(own?.reasons) ? own.reasons : display?.reasons ?? null
  const reasonLine = (token: string | null): string | null => stringOrNull(entryOf(reasons, token)?.line)

  const axes = STATE_AXES.map((key): StateAxisView => {
    const axis = isObject(sm[key]) ? sm[key] : {}
    const words = entryOf(axesWords, key)
    const pos = stringOrNull(words?.pos)
    const neg = stringOrNull(words?.neg)
    const reason = stringOrNull(axis.reason)
    return {
      key,
      name: stringOrNull(words?.name),
      ends: pos === null || neg === null ? null : { pos, neg },
      value: numberOrNull(axis.value),
      confidence: numberOrNull(axis.confidence),
      tier: stringOrNull(axis.evidence_tier),
      reason,
      reasonLine: reasonLine(reason),
    }
  })

  const warningsRaw = sm.warnings
  const warnings = Array.isArray(warningsRaw) ? warningsRaw.filter((w): w is string => typeof w === 'string') : []

  const bs: unknown = result.research.balanceScore
  const trend: StateTrendView | null = !isObject(bs)
    ? null
    : (() => {
      const reason = stringOrNull(bs.reason)
      const words = entryOf(reasons, reason)
      return {
        value: numberOrNull(bs.value),
        nDays: numberOrNull(bs.n_days),
        windowDays: numberOrNull(bs.window_days),
        reason,
        reasonWords: words === null ? null : { word: stringOrNull(words.word), line: stringOrNull(words.line) },
        badge: stringOrNull(bs.badge),
        validated: typeof bs.validated === 'boolean' ? bs.validated : null,
      }
    })()

  return { map, axes, warnings, trend }
}

/**
 * The learning lock (1.9.9 P3, D21), for `/state`: the map is locked until the
 * eyes-closed baseline is confirmed — a baseline with sessions whose flag is
 * `false`, in R37's words — read from `/home.baseline.EC`, never from the
 * reading's own `baseline_status`. Home states the same rule in `mirrorOf`.
 */
export function mapLocked(ec: BaselineStatus): boolean {
  return !(ec.n_sessions !== 0 && ec.low_confidence_flag === false)
}
